// https://github.com/vercel/next.js/blob/8fdb93d76f9692f5f3188259ac643e1d2e960736/examples/api-routes-apollo-server-and-client/apollo/client.js
import React from 'react';
import { ApolloClient, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { useSession, Session, getSession } from 'next-auth/client';
import { GetServerSidePropsContext, GetStaticPropsContext } from 'next';

type ApolloClientType = ApolloClient<NormalizedCacheObject>;
export type SessionType = Session | null | undefined;

type GetStaticApolloClient = (
  context?: GetStaticPropsContext | GetServerSidePropsContext
) => Promise<{
  apolloClient: ApolloClientType;
  session: SessionType;
}>;

let apolloClient: ApolloClientType;

const GRAPHQL_URI = process.env.NEXT_PUBLIC_GRAPHQL_URI;

export const createApolloClient = (accessToken?: string): ApolloClientType =>
  new ApolloClient({
    cache: new InMemoryCache(),
    ssrMode: typeof window === 'undefined',
    link: createUploadLink({
      uri: GRAPHQL_URI,
      credentials: 'same-origin',
      ...(accessToken && { headers: { Authorization: `Bearer ${accessToken}` } }),
    }),
  });

export const initializeApollo = (accessToken?: string, initialState = {}): ApolloClientType => {
  const _apolloClient = createApolloClient(accessToken); // apolloClient ?? createApolloClient(session);

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
};

export const getStaticApolloClient: GetStaticApolloClient = async (context) => {
  const session = context ? await getSession(context as GetServerSidePropsContext) : null;
  const apolloClient = initializeApollo(session?.accessToken);

  return { apolloClient, session };
};

export const useApollo = (initialState: NormalizedCacheObject): ApolloClientType => {
  const [session] = useSession();

  return React.useMemo(() => initializeApollo(session?.accessToken, initialState), [
    session?.accessToken,
    initialState,
  ]);
};
